import { z } from 'zod';

// The shared two-level Immobilientyp taxonomy (IMM-432): a physical category
// plus an optional controlled subtype. Consumed by both the lead and property
// contexts, so it lives in `common/` — neither owns it. en-US wire tokens per
// ADR 0009; the German + English labels live in `@immo/i18n` under
// `common.immobilientyp`, keyed by these tokens.
export const PROPERTY_CATEGORIES = ['APARTMENT', 'HOUSE', 'LAND', 'COMMERCIAL', 'GARAGE_PARKING'] as const;
export const PropertyCategorySchema = z.enum(PROPERTY_CATEGORIES);
export type PropertyCategory = z.infer<typeof PropertyCategorySchema>;

// The most important subtypes per category, OpenImmo / portal aligned. Subtype
// is always optional — a category with no subtype is valid and complete. This
// map is the single source: `PROPERTY_SUBTYPES` and `SUBTYPE_TO_CATEGORY` are
// derived from it, so a subtype belongs to exactly one category by
// construction. The `satisfies` clause makes adding a category a compile error
// until its subtypes are declared.
export const SUBTYPES_BY_CATEGORY = {
  APARTMENT: [
    'FLOOR_APARTMENT',
    'GROUND_FLOOR_APARTMENT',
    'ATTIC_APARTMENT',
    'PENTHOUSE',
    'MAISONETTE',
    'LOFT',
    'BASEMENT_APARTMENT',
    'STUDIO_APARTMENT',
  ],
  HOUSE: [
    'DETACHED_HOUSE',
    'SEMI_DETACHED_HOUSE',
    'TERRACED_HOUSE',
    'VILLA',
    'BUNGALOW',
    'TOWNHOUSE',
    'MULTI_FAMILY_HOUSE',
  ],
  LAND: ['RESIDENTIAL_LAND', 'COMMERCIAL_LAND', 'AGRICULTURAL_LAND'],
  COMMERCIAL: ['OFFICE_PRACTICE', 'RETAIL', 'HOSPITALITY', 'HOTEL', 'INDUSTRIAL', 'SPECIAL_PURPOSE'],
  GARAGE_PARKING: ['GARAGE', 'UNDERGROUND_PARKING', 'OUTDOOR_PARKING', 'CARPORT', 'DUPLEX_PARKING'],
} as const satisfies Record<PropertyCategory, readonly string[]>;

// The flat union of every subtype, in category order. Derived by spreading the
// map so the literal token types are preserved for `PropertySubtype`.
export const PROPERTY_SUBTYPES = [
  ...SUBTYPES_BY_CATEGORY.APARTMENT,
  ...SUBTYPES_BY_CATEGORY.HOUSE,
  ...SUBTYPES_BY_CATEGORY.LAND,
  ...SUBTYPES_BY_CATEGORY.COMMERCIAL,
  ...SUBTYPES_BY_CATEGORY.GARAGE_PARKING,
] as const;
export const PropertySubtypeSchema = z.enum(PROPERTY_SUBTYPES);
export type PropertySubtype = z.infer<typeof PropertySubtypeSchema>;

// Reverse lookup, derived from the same map — a subtype always resolves to its
// one owning category.
export const SUBTYPE_TO_CATEGORY = Object.fromEntries(
  PROPERTY_CATEGORIES.flatMap((category) =>
    SUBTYPES_BY_CATEGORY[category].map((subtype) => [subtype, category] as const),
  ),
) as Record<PropertySubtype, PropertyCategory>;

export function subtypesForCategory(category: PropertyCategory): readonly PropertySubtype[] {
  return SUBTYPES_BY_CATEGORY[category];
}

// A `null` subtype is valid under every category; a non-null subtype is valid
// only under its own category.
export function isValidCategorySubtype(category: PropertyCategory, subtype: PropertySubtype | null): boolean {
  return subtype === null || SUBTYPE_TO_CATEGORY[subtype] === category;
}

// The reusable category + optional-subtype pair. Adopters (Lead in IMM-432.2,
// Property in IMM-432.3) compose this so the pairing is validated at the wire
// boundary rather than merely by convention.
export const ImmobilientypSchema = z
  .object({
    category: PropertyCategorySchema,
    subtype: PropertySubtypeSchema.nullable(),
  })
  .superRefine((value, ctx) => {
    if (!isValidCategorySubtype(value.category, value.subtype)) {
      ctx.addIssue({
        code: 'custom',
        path: ['subtype'],
        message: `Subtype ${value.subtype} does not belong to category ${value.category}`,
      });
    }
  });
export type Immobilientyp = z.infer<typeof ImmobilientypSchema>;

// OpenImmo compatibility reference (IMM-432). No portal-export code exists in
// the repo yet; this table is the documented correspondence a future export
// (step 2) lands on, so the mapping decision is made once here rather than
// rediscovered later. Values approximate the OpenImmo `<objektkategorie>`
// nutzungsart / objektart tokens and are advisory until an export validates
// them.
export const OPENIMMO_CATEGORY_MAP = {
  APARTMENT: 'wohnung',
  HOUSE: 'haus',
  LAND: 'grundstueck',
  COMMERCIAL: 'gewerbe',
  GARAGE_PARKING: 'stellplatz',
} as const satisfies Record<PropertyCategory, string>;

export const OPENIMMO_SUBTYPE_MAP = {
  FLOOR_APARTMENT: 'etage',
  GROUND_FLOOR_APARTMENT: 'erdgeschoss',
  ATTIC_APARTMENT: 'dachgeschoss',
  PENTHOUSE: 'penthouse',
  MAISONETTE: 'maisonette',
  LOFT: 'loft',
  BASEMENT_APARTMENT: 'souterrain',
  STUDIO_APARTMENT: 'apartment',
  DETACHED_HOUSE: 'einfamilienhaus',
  SEMI_DETACHED_HOUSE: 'doppelhaushaelfte',
  TERRACED_HOUSE: 'reihenhaus',
  VILLA: 'villa',
  BUNGALOW: 'bungalow',
  TOWNHOUSE: 'stadthaus',
  MULTI_FAMILY_HOUSE: 'mehrfamilienhaus',
  RESIDENTIAL_LAND: 'wohnen',
  COMMERCIAL_LAND: 'gewerbe',
  AGRICULTURAL_LAND: 'land_und_forstwirtschaft',
  OFFICE_PRACTICE: 'buero_praxen',
  RETAIL: 'einzelhandel',
  HOSPITALITY: 'gastronomie',
  HOTEL: 'hotels',
  INDUSTRIAL: 'hallen_lager_prod',
  SPECIAL_PURPOSE: 'spezialobjekte',
  GARAGE: 'garage',
  UNDERGROUND_PARKING: 'tiefgarage',
  OUTDOOR_PARKING: 'freiplatz',
  CARPORT: 'carport',
  DUPLEX_PARKING: 'duplex',
} as const satisfies Record<PropertySubtype, string>;
